import { useEffect } from "react";
import { SITE } from "../lib/site";

/**
 * Injects premium SEO metadata for ASHIR GROUP:
 * - Local SEO (São José dos Campos + São Paulo Capital)
 * - Keywords: consórcio contemplado, cartas de crédito
 * - JSON-LD: Organization + FinancialService + LocalBusiness + FAQPage
 * - Open Graph + Twitter Card
 */
export default function SEO() {
  useEffect(() => {
    const upsertMeta = (attr, name, content) => {
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const upsertLink = (rel, href) => {
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const title =
      "ASHIR GROUP | Cartas de Crédito e Consórcio Contemplado em São José dos Campos SP";
    const description =
      "ASHIR GROUP — Especialistas em cartas de consórcio contempladas e cartas de crédito para empresas e pessoas físicas. Atendimento em São José dos Campos, São Paulo Capital e todo o Brasil. Capital imediato com juros reduzidos, segurança jurídica e agilidade.";
    const keywords = [
      "consórcio contemplado em São José dos Campos SP",
      "cartas de crédito ASHIR GROUP",
      "carta contemplada Vale do Paraíba",
      "consórcio contemplado São José dos Campos",
      "consórcio Vale do Paraíba",
      "cartas de crédito imobiliário SJC",
      "consórcio contemplado veículos Vale do Paraíba",
      "capital de giro empresa Vale do Paraíba",
      "crédito com juros reduzidos",
      "consórcio contemplado Taubaté Jacareí",
      "ASHIR GROUP",
    ].join(", ");

    document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("name", "keywords", keywords);
    upsertMeta("name", "author", "ASHIR GROUP");
    upsertMeta("name", "robots", "index, follow, max-image-preview:large");
    upsertMeta("name", "language", "pt-BR");
    upsertMeta("name", "geo.region", "BR-SP");
    upsertMeta("name", "geo.placename", "São José dos Campos, São Paulo");
    upsertMeta("name", "geo.position", "-23.2237;-45.9009");
    upsertMeta("name", "ICBM", "-23.2237, -45.9009");

    // Open Graph
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", "ASHIR GROUP");
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:locale", "pt_BR");
    upsertMeta("property", "og:url", "https://ashirfinance.org/");
    upsertMeta(
      "property",
      "og:image",
      "https://images.unsplash.com/photo-1554469384-e58fac16e23a?auto=format&fit=crop&w=1200&q=80"
    );

    // Twitter
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);

    upsertLink("canonical", "https://ashirfinance.org/");

    // JSON-LD
    const jsonld = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": ["Organization", "FinancialService"],
          "@id": "https://ashirfinance.org/#organization",
          name: "ASHIR GROUP",
          alternateName: "Ashir Group Financeira",
          url: "https://ashirfinance.org/",
          logo: "https://ashirfinance.org/logo.png",
          description:
            "Especialistas em cartas de consórcio contempladas e cartas de crédito para empresas e pessoas físicas.",
          areaServed: [
            { "@type": "City", name: "São José dos Campos" },
            { "@type": "City", name: "São Paulo" },
            { "@type": "Country", name: "Brasil" },
          ],
          knowsAbout: [
            "Consórcio contemplado",
            "Cartas de crédito",
            "Capital de giro empresarial",
            "Aquisição patrimonial",
            "Crédito imobiliário",
            "Crédito veicular",
          ],
          sameAs: [SITE.social.instagram, SITE.social.linkedin],
          contactPoint: {
            "@type": "ContactPoint",
            telephone: `+${SITE.whatsappNumber}`,
            contactType: "customer service",
            areaServed: "BR",
            availableLanguage: ["Portuguese"],
          },
        },
        {
          "@type": "LocalBusiness",
          "@id": "https://ashirfinance.org/#localbusiness",
          name: "ASHIR GROUP — Matriz São Paulo",
          image:
            "https://images.unsplash.com/photo-1554469384-e58fac16e23a?auto=format&fit=crop&w=1200&q=80",
          telephone: `+${SITE.whatsappNumber}`,
          email: SITE.email,
          address: {
            "@type": "PostalAddress",
            streetAddress: SITE.address.street,
            addressLocality: "São Paulo",
            addressRegion: "SP",
            postalCode: "04532-060",
            addressCountry: "BR",
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: -23.5859,
            longitude: -46.6789,
          },
          priceRange: "$$$$",
          openingHoursSpecification: [
            {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              opens: "09:00",
              closes: "18:00",
            },
          ],
        },
        {
          "@type": "WebSite",
          "@id": "https://ashirfinance.org/#website",
          url: "https://ashirfinance.org/",
          name: "ASHIR GROUP",
          publisher: { "@id": "https://ashirfinance.org/#organization" },
          inLanguage: "pt-BR",
        },
        {
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "O que é uma carta de consórcio contemplada?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "É uma carta de crédito de consórcio já contemplada (por sorteio ou lance), que pode ser transferida formalmente a um novo titular para uso imediato do crédito, com respaldo da administradora.",
              },
            },
            {
              "@type": "Question",
              name: "A ASHIR GROUP atende em São José dos Campos SP?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Sim. Atendemos em São José dos Campos, São Paulo Capital e em todo o Brasil, com operações estruturadas e atendimento consultivo.",
              },
            },
            {
              "@type": "Question",
              name: "Quais vantagens em relação ao crédito bancário tradicional?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Custo financeiro significativamente menor, sem juros abusivos, parcelas planejáveis, flexibilidade para empresas e pessoas físicas, e formalização rápida junto à administradora.",
              },
            },
          ],
        },
      ],
    };

    let script = document.getElementById("ashir-jsonld");
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "ashir-jsonld";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(jsonld);
  }, []);

  return null;
}
